/** FELVORA — Inmobiliaria F6 P1 · Arrendamientos: lista + detalle con tabla de pagos +
 *  semáforo screening + vigencias + registrar pago con desglose de forma de pago.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileSignature, Plus, DollarSign, ShieldAlert, RefreshCw, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
const ESTATUS_COLOR = {
  vigente: "bg-emerald-100 text-emerald-800",
  por_vencer: "bg-amber-100 text-amber-800",
  vencido: "bg-red-100 text-red-800",
  renovado: "bg-blue-100 text-blue-800",
  terminado: "bg-slate-100 text-slate-700",
  borrador: "bg-slate-100 text-slate-500",
};

export default function Arrendamientos() {
  const [items, setItems] = useState([]);
  const [inmuebles, setInmuebles] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [openNuevo, setOpenNuevo] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [nuevo, setNuevo] = useState({
    inmueble_id: "", inquilino_cliente_id: "",
    fecha_inicio: "", fecha_fin: "",
    renta_mensual_sin_iva: 0, aplica_iva: false, iva_mensual: 0,
    deposito_garantia: 0, moratorio_tasa: 0, moratorio_pena_fija: 0,
    ajuste_anual_tipo: "inpc", ajuste_anual_pct: 0,
  });

  const cargar = () => {
    api.get("/inmobiliaria/arrendamientos").then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(() => {
    cargar();
    api.get("/inmobiliaria/inmuebles").then((r) => setInmuebles(r.data)).catch(() => {});
    api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {});
  }, []);

  const guardar = async () => {
    try {
      const body = {
        ...nuevo,
        renta_mensual_sin_iva: parseFloat(nuevo.renta_mensual_sin_iva) || 0,
        iva_mensual: nuevo.aplica_iva
          ? (parseFloat(nuevo.iva_mensual) || Math.round((parseFloat(nuevo.renta_mensual_sin_iva) || 0) * 0.16 * 100) / 100)
          : 0,
      };
      await api.post("/inmobiliaria/arrendamientos", body);
      toast.success("Contrato creado");
      setOpenNuevo(false); cargar();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.mensaje || "Error");
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="arrendamientos-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <FileSignature className="mr-2 inline h-6 w-6 text-emerald-700" />
            Arrendamientos (Fr. XV)
          </h1>
          <p className="text-sm text-slate-500">Contratos, tabla de pagos, cobranza y renovaciones — LFPIORPI Art. 17 XV.</p>
        </div>
        <Button data-testid="btn-nuevo-arrendamiento" onClick={() => setOpenNuevo(true)}>
          <Plus className="mr-1 h-4 w-4" /> Nuevo contrato
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {items.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Sin contratos aún.</p>}
            {items.map((c) => {
              const inm = inmuebles.find((i) => i.id === c.inmueble_id);
              const inq = clientes.find((x) => x.id === c.inquilino_cliente_id);
              return (
                <div key={c.id} data-testid={`arrend-${c.id}`}
                     className="cursor-pointer p-3 hover:bg-slate-50"
                     onClick={() => setDetalle(c)}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{inm?.direccion || c.inmueble_id.slice(0,8)}</p>
                      <p className="text-xs text-slate-500">
                        Inquilino: {inq?.nombre || c.inquilino_cliente_id.slice(0,8)} · Renta: {mxn(c.renta_mensual_con_iva)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={ESTATUS_COLOR[c.estatus]}>{c.estatus}</Badge>
                      <Badge variant="outline">Vigencia v{c.vigencia_actual} · {c.fecha_inicio} → {c.fecha_fin}</Badge>
                      {c.gates_creacion?.umbral_fr_xv?.identificacion && (
                        <Badge className="bg-amber-100 text-amber-800">Identificación Fr. XV</Badge>
                      )}
                      {c.gates_creacion?.umbral_fr_xv?.aviso && (
                        <Badge className="bg-red-100 text-red-800">Aviso Fr. XV</Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Modal nuevo contrato */}
      <Dialog open={openNuevo} onOpenChange={setOpenNuevo}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nuevo contrato de arrendamiento</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className="text-sm">Inmueble</label>
              <Select value={nuevo.inmueble_id} onValueChange={(v) => setNuevo({...nuevo, inmueble_id: v})}>
                <SelectTrigger data-testid="select-inmueble"><SelectValue placeholder="Elige inmueble" /></SelectTrigger>
                <SelectContent>{inmuebles.map(i => <SelectItem key={i.id} value={i.id}>{i.direccion}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-sm">Inquilino (cliente)</label>
              <Select value={nuevo.inquilino_cliente_id} onValueChange={(v) => setNuevo({...nuevo, inquilino_cliente_id: v})}>
                <SelectTrigger data-testid="select-inquilino"><SelectValue placeholder="Elige inquilino" /></SelectTrigger>
                <SelectContent>{clientes.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre || c.razon_social}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-sm">Fecha inicio</label>
              <Input type="date" data-testid="input-fecha-inicio" value={nuevo.fecha_inicio} onChange={(e) => setNuevo({...nuevo, fecha_inicio: e.target.value})} /></div>
            <div><label className="text-sm">Fecha fin</label>
              <Input type="date" data-testid="input-fecha-fin" value={nuevo.fecha_fin} onChange={(e) => setNuevo({...nuevo, fecha_fin: e.target.value})} /></div>
            <div><label className="text-sm">Renta mensual (sin IVA)</label>
              <Input type="number" data-testid="input-renta" value={nuevo.renta_mensual_sin_iva} onChange={(e) => setNuevo({...nuevo, renta_mensual_sin_iva: e.target.value})} /></div>
            <div><label className="text-sm">Depósito en garantía</label>
              <Input type="number" data-testid="input-deposito" value={nuevo.deposito_garantia} onChange={(e) => setNuevo({...nuevo, deposito_garantia: parseFloat(e.target.value) || 0})} /></div>
            <div><label className="text-sm">Moratorio (tasa anual %)</label>
              <Input type="number" data-testid="input-mora-tasa" value={nuevo.moratorio_tasa} onChange={(e) => setNuevo({...nuevo, moratorio_tasa: parseFloat(e.target.value) || 0})} /></div>
            <div><label className="text-sm">Pena fija por atraso</label>
              <Input type="number" data-testid="input-mora-pena" value={nuevo.moratorio_pena_fija} onChange={(e) => setNuevo({...nuevo, moratorio_pena_fija: parseFloat(e.target.value) || 0})} /></div>
            <div><label className="text-sm">Ajuste anual</label>
              <Select value={nuevo.ajuste_anual_tipo} onValueChange={(v) => setNuevo({...nuevo, ajuste_anual_tipo: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inpc">INPC</SelectItem>
                  <SelectItem value="porcentaje">Porcentaje fijo</SelectItem>
                </SelectContent>
              </Select></div>
            <div><label className="text-sm">% ajuste anual</label>
              <Input type="number" data-testid="input-ajuste" value={nuevo.ajuste_anual_pct} onChange={(e) => setNuevo({...nuevo, ajuste_anual_pct: parseFloat(e.target.value) || 0})} /></div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={nuevo.aplica_iva} onChange={(e) => setNuevo({...nuevo, aplica_iva: e.target.checked})} data-testid="chk-iva" />
              <span className="text-sm">Aplica IVA (16% sobre la renta)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNuevo(false)}>Cancelar</Button>
            <Button data-testid="btn-guardar-contrato" onClick={guardar}>Crear contrato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal detalle */}
      <Dialog open={!!detalle} onOpenChange={(v) => !v && setDetalle(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detalle del contrato</DialogTitle></DialogHeader>
          {detalle && <DetalleContrato contrato={detalle} onReload={() => { cargar(); api.get(`/inmobiliaria/arrendamientos/${detalle.id}`).then(r => setDetalle(r.data)); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetalleContrato({ contrato, onReload }) {
  const [tab, setTab] = useState("tabla");
  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagos, setPagos] = useState([]);
  const [renovarOpen, setRenovarOpen] = useState(false);
  const [inpcPct, setInpcPct] = useState(5);
  const [pago, setPago] = useState({
    monto: 0, forma_pago: "transferencia", meses_cubiertos: 1,
    monto_efectivo_con_iva: 0, fecha: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    api.get(`/inmobiliaria/arrendamientos/${contrato.id}/pagos`)
      .then((r) => setPagos(r.data)).catch(() => {});
  }, [contrato.id]);

  const registrarPago = async () => {
    try {
      const r = await api.post(`/inmobiliaria/arrendamientos/${contrato.id}/pago`, {
        ...pago, monto: parseFloat(pago.monto) || 0,
        monto_efectivo_con_iva: parseFloat(pago.monto_efectivo_con_iva) || 0,
      });
      toast.success(`Pago aplicado · ${r.data.objeto_de_aviso_fr_xv ? "OBJETO DE AVISO Fr. XV" : "Sin aviso"}`);
      setPagoOpen(false); onReload();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.mensaje || "Error");
      toast.error(msg);
    }
  };

  const renovar = async () => {
    try {
      const r = await api.post(`/inmobiliaria/arrendamientos/${contrato.id}/renovar`, { inpc_pct: parseFloat(inpcPct) || 0, meses: 12 });
      toast.success(`Renovado · nueva renta ${new Intl.NumberFormat("es-MX",{style:"currency",currency:"MXN"}).format(r.data.renta_nueva_con_iva)}`);
      setRenovarOpen(false); onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap gap-2">
        <Badge className={ESTATUS_COLOR[contrato.estatus]}>{contrato.estatus}</Badge>
        <Badge variant="outline">Renta: {mxn(contrato.renta_mensual_con_iva)}</Badge>
        <Badge variant="outline">Depósito: {mxn(contrato.deposito_garantia)}</Badge>
        <Badge variant="outline">Vigencia v{contrato.vigencia_actual}: {contrato.fecha_inicio} → {contrato.fecha_fin}</Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="tabla" data-testid="tab-tabla">Tabla de pagos</TabsTrigger>
          <TabsTrigger value="pagos" data-testid="tab-pagos">Pagos ({pagos.length})</TabsTrigger>
          <TabsTrigger value="vigencias" data-testid="tab-vigencias">Vigencias ({contrato.vigencias?.length || 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="tabla" className="max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50">
              <tr><th className="text-left">#</th><th className="text-left">Vencimiento</th>
                  <th className="text-right">Renta</th><th className="text-right">IVA</th>
                  <th className="text-right">Pagado</th><th>Estatus</th></tr>
            </thead>
            <tbody>
              {(contrato.tabla_pagos || []).map((c) => (
                <tr key={c.no} className="border-t border-slate-100" data-testid={`cuota-${c.no}`}>
                  <td>{c.no}</td>
                  <td>{c.fecha_pago}</td>
                  <td className="text-right">{mxn(c.capital)}</td>
                  <td className="text-right">{mxn(c.iva_interes)}</td>
                  <td className="text-right">{mxn(c.pagado_acumulado)}</td>
                  <td><Badge variant="outline">{c.estatus}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabsContent>
        <TabsContent value="pagos">
          {pagos.length === 0 ? <p className="italic text-slate-400">Sin pagos aún.</p> :
            <div className="max-h-64 overflow-y-auto divide-y">
              {pagos.map((p) => (
                <div key={p.id} className="py-2" data-testid={`pago-${p.id}`}>
                  <div className="flex justify-between">
                    <span>{p.fecha} · {p.forma_pago} · meses {p.meses_cubiertos}</span>
                    <span className="font-semibold">{mxn(p.monto)}</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Aplicación: cap {mxn(p.aplicacion.capital)} · IVA {mxn(p.aplicacion.iva)} · int {mxn(p.aplicacion.interes)} · mora {mxn(p.aplicacion.moratorio)}
                    {p.saldo_a_favor > 0 && ` · saldo a favor ${mxn(p.saldo_a_favor)}`}
                  </p>
                </div>
              ))}
            </div>
          }
        </TabsContent>
        <TabsContent value="vigencias">
          <div className="divide-y">
            {(contrato.vigencias || []).map((v) => (
              <div key={v.vigencia_no} className="py-2" data-testid={`vig-${v.vigencia_no}`}>
                <span className="font-semibold">v{v.vigencia_no}</span> · {v.fecha_inicio} → {v.fecha_fin} · Renta {mxn(v.renta_mensual_con_iva)}
                {v.ajuste_pct !== null && <span className="text-xs text-slate-500 ml-2">Ajuste {v.ajuste_pct}%</span>}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
        {contrato.estatus === "vigente" && (
          <>
            <Button data-testid="btn-registrar-pago" onClick={() => setPagoOpen(true)}>
              <DollarSign className="mr-1 h-4 w-4" /> Registrar pago
            </Button>
            <Button variant="outline" data-testid="btn-renovar" onClick={() => setRenovarOpen(true)}>
              <RefreshCw className="mr-1 h-4 w-4" /> Renovar (INPC)
            </Button>
          </>
        )}
      </div>

      <Dialog open={pagoOpen} onOpenChange={setPagoOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar pago de renta</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm">Monto</label>
              <Input type="number" data-testid="input-monto-pago" value={pago.monto} onChange={(e) => setPago({...pago, monto: e.target.value})} /></div>
            <div><label className="text-sm">Meses cubiertos</label>
              <Input type="number" data-testid="input-meses" value={pago.meses_cubiertos} onChange={(e) => setPago({...pago, meses_cubiertos: parseInt(e.target.value) || 1})} /></div>
            <div><label className="text-sm">Forma de pago</label>
              <Select value={pago.forma_pago} onValueChange={(v) => setPago({...pago, forma_pago: v})}>
                <SelectTrigger data-testid="select-forma-pago"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="mixto">Mixto</SelectItem>
                </SelectContent>
              </Select></div>
            <div><label className="text-sm">Fecha</label>
              <Input type="date" value={pago.fecha} onChange={(e) => setPago({...pago, fecha: e.target.value})} /></div>
            {(pago.forma_pago === "efectivo" || pago.forma_pago === "mixto") && (
              <div className="col-span-2 rounded border border-amber-300 bg-amber-50 p-2">
                <label className="text-sm font-semibold text-amber-800">
                  <Ban className="inline h-4 w-4 mr-1" />Candado Art. 32-VII: efectivo con IVA
                </label>
                <Input type="number" data-testid="input-efectivo" value={pago.monto_efectivo_con_iva}
                        onChange={(e) => setPago({...pago, monto_efectivo_con_iva: e.target.value})} />
                <p className="text-xs text-slate-500">Umbral: 3,210 UMA · el motor bloquea si excede.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagoOpen(false)}>Cancelar</Button>
            <Button data-testid="btn-guardar-pago" onClick={registrarPago}>Aplicar pago</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renovarOpen} onOpenChange={setRenovarOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renovar contrato (INPC 12m)</DialogTitle></DialogHeader>
          <div>
            <label className="text-sm">% INPC (ajuste anual)</label>
            <Input type="number" data-testid="input-inpc" value={inpcPct} onChange={(e) => setInpcPct(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenovarOpen(false)}>Cancelar</Button>
            <Button data-testid="btn-confirmar-renovar" onClick={renovar}>Renovar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
